import {
  type Appointment,
  type ExtendedAppointment,
  asociateAppointment,
  assignAppointment,
  convertAppointmentToProject,
  createAndUpdateTask,
  deleteDropInAppointment,
  dissasociateAppointment,
  getUserInfo,
  updateAppointments,
  updateDropInAppointments,
  useTeam,
  useUserDetails,
} from "@repo/api";
import { t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useNavigate } from "react-router-dom";

import { useModals } from "../modals/hooks";
import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
} from "./modals/components/AppointmentModalReducer";
import { isActiveStatus } from "./utils/isActiveStatus";

const getLinkWithLanguage = (
  market: string,
  language: string,
  link?: string
) => {
  if (!link) {
    return undefined;
  }
  const regex = new RegExp(`/${market}/([a-z]{2})/`);
  return link.replace(regex, `/${market}/${language}/`);
};

const getChangedFields = <T extends object>(
  original: Partial<T> | undefined,
  updated: Partial<T>
) => {
  const changed: Partial<T> = {};

  for (const [key, value] of Object.entries(
    updated as Record<string, unknown>
  )) {
    const originalValue = (original as Record<string, unknown> | undefined)?.[
      key
    ];
    if (JSON.stringify(originalValue) !== JSON.stringify(value)) {
      (changed as Record<string, unknown>)[key] = value;
    }
  }

  return changed;
};

export const useAppointmentActions = (
  state: AppointmentModalState,
  actions: AppointmentModalStateActions,
  onClose: () => void,
  mutateSearchAppointments?: () => void
) => {
  const { market, language } = useSettings();
  const { pushModal, hideModal } = useModals();
  const { updateToast } = useToast();
  const navigate = useNavigate();
  const { data: assignedCoworker } = useUserDetails(
    state.appointment?.assignedCoworker
  );
  const { data: assignedTeam } = useTeam(state.appointment?.assignedTeam);

  const isDropIn = state.appointment?.type === "DROP_IN";

  const link = getLinkWithLanguage(
    market,
    language,
    state.appointment?.appointmentLink
  );
  const openLink = () => window.open(link, "_blank");

  const disableAction = !isActiveStatus(state.appointment?.appointmentStatus);
  const disableCancel =
    state.appointment?.paymentStatus === "PAID" ||
    state.appointment?.appointmentStatus === "STARTED" ||
    !isActiveStatus(state.appointment?.appointmentStatus);

  const disableNoShow =
    state.appointment?.appointmentStatus === "STARTED" ||
    !isActiveStatus(state.appointment?.appointmentStatus);

  const disableAssign =
    !isActiveStatus(state.appointment?.appointmentStatus) ||
    state.appointment?.appointmentStatus === "STARTED";

  const disableMove =
    !state.opportunityId ||
    !isActiveStatus(state.appointment?.appointmentStatus);

  const cancelDropIn = async (noShow?: boolean) => {
    if (!state.appointment) {
      return;
    }
    if (isDropIn) {
      return await updateDropInAppointments(
        {
          ...state.appointment,
          appointmentStatus: noShow ? "CANCELLED_NO_SHOW" : "CANCELLED",
          countryCode: market.toUpperCase(),
        },
        mutateSearchAppointments
      );
    }
  };
  const showBokaLink =
    Boolean(state.appointment?.appointmentLink) &&
    !isActiveStatus(state.appointment?.appointmentStatus);

  const onSelectProject = (projectId: number) => {
    pushModal?.({
      type: "CONFIRM",
      onConfirm: () => {
        onMoveAppointment?.(projectId);
        hideModal?.("NEW_PROJECT");
      },
      title: t("action.move_appointment"),
      message: t("prompt.confirm_move_appointment", {
        projectId,
      }),
    });
  };

  const onProjectCreated = (projectId: number) => {
    onMoveAppointment?.(projectId);
    hideModal?.("NEW_PROJECT");
  };

  const onAssign = () => {
    pushModal?.({
      type: "SELECT_TEAM_OR_COWORKER",
      selectionMode: isDropIn
        ? { coworkerSelectionMode: "SINGLE", selectOnlyTeam: true }
        : { coworkerSelectionMode: "SINGLE" },
      selected:
        isDropIn && assignedTeam
          ? {
              kind: "team",
              team: assignedTeam,
              assignedToTeam: true,
              members: (assignedTeam.teamMembers ?? []).map((m) => ({
                ...m,
                selected: true,
              })),
            }
          : assignedCoworker
            ? [
                getUserInfo({
                  ...assignedCoworker,
                  mail: state.appointment?.assignedCoworker,
                }),
              ]
            : undefined,
      setSelected: (selected) => {
        const coworkers = Array.isArray(selected) ? selected : undefined;
        actions.setLoading("ASSIGNING");
        if (isDropIn) {
          updateDropInAppointments(
            {
              ...state.appointment,
              assignedCoworker: coworkers?.[0].emailId,
              countryCode: market.toUpperCase(),
            } as ExtendedAppointment,
            mutateSearchAppointments
          )
            .then(() => {
              updateToast({
                id: state.appointment?.id,
                title: t("prompt.appointment_assigned"),
              });
            })
            .catch((err) => {
              updateToast({
                id: state.appointment?.id,
                title: t("prompt.appointment_assign_failed"),
                message: getErrorMessage(err),
                type: "error",
              });
            })
            .finally(() => {
              actions.setLoading(undefined);
              onClose();
            });
        } else {
          assignAppointment(
            state.appointment?.appointmentId,
            state.appointment?.opportunityId,
            state.appointment?.assignedCoworker,
            coworkers?.[0].emailId,
            mutateSearchAppointments
          )
            .then(() => {
              updateToast({
                id: state.appointment?.id,
                title: t("prompt.appointment_assigned"),
              });
            })
            .catch((err) => {
              updateToast({
                id: state.appointment?.id,
                title: t("prompt.appointment_assign_failed"),
                message: getErrorMessage(err),
                type: "error",
              });
            })
            .finally(() => {
              actions.setLoading(undefined);
              onClose();
            });
        }
      },
      searchContext: state.context,
    });

    trackAnalytics("assign_appointmnet");
  };

  const onUpdate = () => {
    if (!state.updatedTask && !state.updatedAppointment) {
      return;
    }
    actions.setLoading("UPDATING");
    if (state.updatedTask) {
      createAndUpdateTask(state.updatedTask)
        .then(() => {
          updateToast({
            id: state.appointment?.id,
            title: t("prompt.appointment_saved"),
          });
        })
        .catch((err) => {
          updateToast({
            id: state.appointment?.id,
            title: t("prompt.appointment_saved_failed"),
            message: getErrorMessage(err),
            type: "error",
          });
        })
        .finally(() => {
          actions.setLoading(undefined);
          onClose();
        });
    } else if (state.updatedAppointment) {
      const changedUpdates = getChangedFields(
        state.appointment,
        state.updatedAppointment
      );

      const completeAppointmentPayload: Appointment = {
        ...(state.appointment as Appointment),
        ...changedUpdates,
      };

      updateDropInAppointments(
        completeAppointmentPayload,
        mutateSearchAppointments
      )
        .then((res) => {
          actions.setAppointment(res);
          updateToast({
            id: state.appointment?.id,
            title: t("prompt.appointment_saved"),
          });
        })
        .catch((err) => {
          updateToast({
            id: state.appointment?.id,
            title: t("prompt.appointment_saved_failed"),
            message: getErrorMessage(err),
            type: "error",
          });
        })
        .finally(() => {
          actions.setLoading(undefined);
          actions.setStep("VIEW");
        });
    }
  };

  const onDelete = () => {
    const appointmentId = state.appointment?.id;
    if (!appointmentId) {
      return;
    }

    actions.setLoading("DELETING");
    const deletePromise = appointmentId
      ? deleteDropInAppointment(
          appointmentId,
          state.appointment?.opportunityId,
          mutateSearchAppointments
        )
      : Promise.resolve();
    deletePromise
      .then(() => {
        actions.setLoading(undefined);
        updateToast({
          id: state.appointment?.id,
          title: t("prompt.appointment_deleted"),
        });
      })
      .catch((err) => {
        actions.setLoading(undefined);
        updateToast({
          id: state.appointment?.id,
          title: t("prompt.appointment_delete_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      })
      .finally(() => onClose());
  };

  const onCancel = (action?: "NO_SHOW") => {
    pushModal({
      type: "CONFIRM",
      onConfirm: () => {
        actions.setLoading(action === "NO_SHOW" ? "NO_SHOW" : "CANCELLING");

        const apiCall = isDropIn
          ? cancelDropIn(action === "NO_SHOW")
          : updateAppointments(
              action === "NO_SHOW" ? "no-show" : "cancel",
              state.appointment
            );

        apiCall.finally(() => {
          actions.setLoading(undefined);
          onClose();
        });
      },
      title:
        action === "NO_SHOW"
          ? t("label.no_show")
          : t("action.cancel_appointment"),
      message:
        action === "NO_SHOW"
          ? t("prompt.confirm_appointment_no_show")
          : t("prompt.confirm_cancel_appointment"),
    });
  };

  const onMoveAppointment = async (
    newProjectId?: number,
    createNewProject?: boolean
  ) => {
    let theNewProjectId;

    try {
      actions.setLoading("MOVING_APPOINTMENT");

      if (state.opportunityId) {
        await dissasociateAppointment(
          state.opportunityId,
          state.appointment?.id
        );
      }

      if (newProjectId) {
        theNewProjectId = newProjectId;
        await asociateAppointment(newProjectId, state.appointment?.id);
      }

      if (createNewProject) {
        const newProject = await convertAppointmentToProject(
          state.appointment?.id,
          "internalAppointmentId"
        );
        theNewProjectId = newProject?.id;
      }

      updateToast({
        id: state.appointment?.id,
        title: t("prompt.appointment_saved"),
      });
    } catch (err) {
      updateToast({
        id: state.appointment?.id,
        title: t("prompt.appointment_saved_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      actions.setLoading(undefined);
      onClose();
      if (newProjectId !== undefined) {
        navigate(`/opportunity/${theNewProjectId}`);
      }
    }
  };

  return {
    openLink,
    disableAction,
    disableCancel,
    disableNoShow,
    disableAssign,
    disableMove,
    cancelDropIn,
    showBokaLink,
    onSelectProject,
    onProjectCreated,
    onAssign,
    onUpdate,
    onDelete,
    onCancel,
    onMoveAppointment,
  };
};
